import React from 'react'
import { Box, Typography, Button } from '@mui/material'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import DeliveryDiningOutlinedIcon from '@mui/icons-material/DeliveryDiningOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler'
import paymentLabel from '../../utils/paymentLabel'
import { dateIn, dateTimeIn, orderZone, timeIn } from '../../utils/restaurantTime'
import { ops, FS, tabular, btn, ORDER_TONE } from './opsTokens'
import {
  OrderCardShell, CardBody, CardHeader, CardFooter, StatusChip, FulfilmentTag, CustomerLine,
  ItemsLine, Callout, DriverStrip, InitialsAvatar, MicroTag, LinkChip, CateringTag
} from './OpsUi'
import { canHaveRider, inStoreLabel } from '../../utils/orderFulfilment'

/* Label + tone per marketplace orderStatus. Tones come from the one semantic
   map in opsTokens.js, so this card, the filter pills and the modal agree. */
export const STATUS_CONFIG = {
  SCHEDULED: { label: 'Scheduled', tone: ORDER_TONE.SCHEDULED },
  PENDING: { label: 'New Order', tone: ORDER_TONE.PENDING },
  ACCEPTED: { label: 'Accepted', tone: ORDER_TONE.ACCEPTED },
  PREPARED: { label: 'Ready', tone: ORDER_TONE.PREPARED },
  ASSIGNED: { label: 'Rider Assigned', tone: ORDER_TONE.ASSIGNED },
  PICKED: { label: 'On The Way', tone: ORDER_TONE.PICKED },
  DELIVERED: { label: 'Delivered', tone: ORDER_TONE.DELIVERED },
  CANCELLED: { label: 'Cancelled', tone: ORDER_TONE.CANCELLED }
}

/* Refunded, however it happened: a support ticket resolved with a refund, a
   full refund through QorCommerce, Stripe or the POS (each sets paymentStatus
   REFUNDED), or any amount refunded — totalRefunded counts the ticket's wallet
   credit plus card refunds, partial ones included (Stripe's live on the split
   ledger, QorCommerce's and the POS's on the order). */
export const isRefundedOrder = o =>
  o.supportTicketStatus === 'RESOLVED' || o.paymentStatus === 'REFUNDED' ||
  Number(o.refundedAmount) > 0 || Number(o.totalRefunded) > 0

/* What the order came to — the same Total the detail modal prints. placeOrder
   stores orderAmount net of wallet credit, so the wallet is added back: it is
   how the customer paid, not money off. serviceFee is the fee the card was
   charged (0 for cash); leaving it out put the list a fee short of the modal. */
export const orderTotal = o =>
  (o.orderAmount || 0) +
  (o.walletAmountUsed || 0) +
  (o.deliveryCharges || 0) +
  (o.tipping || 0) +
  (o.taxationAmount || 0) +
  (parseFloat(o.serviceFee) || 0)

const timeAgo = (dateStr, tz) => {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ${mins % 60}m ago`
  return dateIn(dateStr, tz)
}

const phoneLinkSx = {
  display: 'inline-flex', alignItems: 'center', gap: 0.4, flexShrink: 0,
  fontSize: FS.xs, fontWeight: 600, color: ops.text2, textDecoration: 'none', ...tabular,
  '& svg': { fontSize: 14, color: ops.muted },
  '&:hover': { color: ops.text }
}

const OrderCard = ({
  order,
  onViewDetail,
  onAccept,
  onMarkPrepared,
  onCancel,
  onAssignRider,
  isSuperAdmin,
  currencySymbol = '$'
}) => {
  const config = STATUS_CONFIG[order.orderStatus] || {
    label: order.orderStatus,
    tone: 'neutral'
  }
  // For pickup orders that are DELIVERED, show "Picked Up" not "Delivered"
  const statusLabel = (order.orderStatus === 'DELIVERED' && order.isPickedUp)
    ? 'Picked Up'
    : config.label

  const totalAmount = orderTotal(order)

  const items = order.items || []
  const itemsSummary = items
    .slice(0, 2)
    .map(
      item =>
        `${item.quantity}x ${item.title}${item.variation?.title ? ` (${item.variation.title})` : ''}`
    )
    .join(', ')

  return (
    <OrderCardShell
      toneName={config.tone}
      label={`Order #${order.orderId}, ${statusLabel}`}
      onOpen={() => onViewDetail && onViewDetail(order)}
    >
      <CardBody>
        {/* Header */}
        <CardHeader
          id={`#${order.orderId}`}
          time={
            <>
              <AccessTimeRoundedIcon sx={{ fontSize: 13 }} />
              <Typography component="span" sx={{ fontSize: FS.xs, color: ops.muted, ...tabular }}>
                {timeAgo(order.createdAt, orderZone(order))}
              </Typography>
            </>
          }
          right={
            <>
              <StatusChip toneName={config.tone} label={statusLabel} />
              {order.isCatering && <CateringTag />}
              {order.supportTicketStatus === 'RESOLVED' && (
                <StatusChip size="sm" toneName="refunded" label="Refunded" />
              )}
              {!order.supportTicketStatus && isRefundedOrder(order) && (
                <StatusChip size="sm" toneName="refunded" label="Refunded" />
              )}
              {order.supportTicketStatus === 'CLOSED_NO_ACTION' && (
                <StatusChip size="sm" toneName="slate" label="Claim Denied" />
              )}
              {order.supportTicketStatus && !['RESOLVED','CLOSED_NO_ACTION'].includes(order.supportTicketStatus) && (
                <StatusChip size="sm" toneName="amber" label="Ticket Open" />
              )}
            </>
          }
        />

        {/* Meta: fulfilment + restaurant (super admin only) */}
        <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap" minWidth={0}>
          <FulfilmentTag kind={order.isPickedUp || inStoreLabel(order) ? 'pickup' : 'delivery'}>
            {order.isPickedUp && !inStoreLabel(order)
              ? 'Pickup'
              : inStoreLabel(order) || (() => {
                  // Cascade-aware (rider > FD > self-delivery > default)
                  if (order.rider?.driverType === 'RESTAURANT') return 'Restaurant Delivery'
                  if (order.rider?.driverType === 'RUSHMYFOOD') return 'RushMyFood Delivery'
                  if (order.firstDeliveryOrderId) return 'RushMyFood Delivery'
                  if (order.isSelfDelivery) return 'Restaurant Delivery'
                  return 'RushMyFood Delivery'
                })()}
          </FulfilmentTag>
          {order.orderSource === 'MANUAL' && <FulfilmentTag kind="delivery">Phone order</FulfilmentTag>}
          {isSuperAdmin && order.restaurant?.name && (
            <Box display="flex" alignItems="center" gap={0.5} minWidth={0} sx={{ color: ops.muted }}>
              <StorefrontOutlinedIcon sx={{ fontSize: 14, flexShrink: 0 }} />
              <Typography noWrap sx={{ fontSize: FS.sm, color: ops.text2, fontWeight: 500 }}>
                {order.restaurant.name}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Customer */}
        <CustomerLine name={order.user?.name} phone={order.user?.phone} />

        {/* Items */}
        <ItemsLine text={itemsSummary} more={items.length - 2} />

        {/* Scheduled delivery time banner */}
        {order.orderStatus === 'SCHEDULED' && order.orderDate && (
          <Callout toneName="slate" icon={<CalendarMonthOutlinedIcon />}>
            {order.isPickedUp ? 'Pickup' : 'Delivery'} at {dateTimeIn(order.orderDate, orderZone(order))}
          </Callout>
        )}
      </CardBody>

      <CardFooter
        left={
          <>
            <Typography sx={{ fontSize: FS.xs, color: ops.muted, ...tabular }}>
              {items.length} item{items.length === 1 ? '' : 's'}
            </Typography>
            <Typography noWrap sx={{ fontSize: FS.xs, color: ops.muted, ...tabular }}>
              {paymentLabel(order)}
            </Typography>
          </>
        }
        total={`${currencySymbol}${totalAmount.toFixed(2)}`}
      >
        {/* Rider / FD Driver Info */}
        {order.firstDeliveryOrderId && (order.fdDriverName || order.orderStatus === 'PREPARED') && (
          <DriverStrip>
            <Box display="flex" alignItems="center" gap={1} minWidth={0}>
              <InitialsAvatar name={order.fdDriverName || '?'} src={order.fdDriverPhoto} size={26} />
              <Box display="flex" alignItems="center" gap={0.6} flex={1} minWidth={0}>
                <Typography noWrap sx={{ fontSize: FS.sm, fontWeight: 600, color: ops.text, minWidth: 0 }}>
                  {order.fdDriverName || 'Awaiting driver…'}
                </Typography>
                {order.fdDriverTransport ? <MicroTag>{order.fdDriverTransport.toUpperCase()}</MicroTag> : null}
                <MicroTag>RUSHMYFOOD</MicroTag>
              </Box>
            </Box>
            {order.fdDriverPhone && (
              <Box pl={4.25}>
                <Box component="a" href={`tel:${order.fdDriverPhone}`} onClick={e => e.stopPropagation()}
                  aria-label={`Call driver ${order.fdDriverPhone}`} sx={phoneLinkSx}>
                  <PhoneOutlinedIcon /> {order.fdDriverPhone}
                </Box>
              </Box>
            )}
            {(order.fdPickupEta || order.fdDropoffEta) && (
              <Box display="flex" gap={1.5} pl={4.25} flexWrap="wrap">
                {order.fdPickupEta && (
                  <Typography sx={{ fontSize: FS.label, color: ops.muted, fontWeight: 600, letterSpacing: '0.03em', ...tabular }}>
                    PICKUP <Box component="span" sx={{ color: ops.text }}>{timeIn(order.fdPickupEta, orderZone(order))}</Box>
                  </Typography>
                )}
                {order.fdDropoffEta && (
                  <Typography sx={{ fontSize: FS.label, color: ops.muted, fontWeight: 600, letterSpacing: '0.03em', ...tabular }}>
                    DROPOFF <Box component="span" sx={{ color: ops.text }}>{timeIn(order.fdDropoffEta, orderZone(order))}</Box>
                  </Typography>
                )}
              </Box>
            )}
            {(order.fdTrackerLink || order.fdDspTrackerLink) && (
              <Box display="flex" gap={0.75} pl={4.25} flexWrap="wrap">
                {order.fdTrackerLink && (
                  <LinkChip
                    href={order.fdTrackerLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    icon={<OpenInNewIcon />}
                  >
                    Tracker 1
                  </LinkChip>
                )}
                {order.fdDspTrackerLink && (
                  <LinkChip
                    href={order.fdDspTrackerLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    icon={<OpenInNewIcon />}
                  >
                    Tracker 2
                  </LinkChip>
                )}
              </Box>
            )}
          </DriverStrip>
        )}
        {!order.firstDeliveryOrderId && order.rider && (
          <DriverStrip sx={{ flexDirection: 'row', alignItems: 'center', gap: 1 }}>
            <InitialsAvatar name={order.rider.name || '?'} src={order.rider.pictureUrl} size={26} />
            <Box display="flex" alignItems="center" gap={0.6} flex={1} minWidth={0}>
              <Typography noWrap sx={{ fontSize: FS.sm, fontWeight: 600, color: ops.text, minWidth: 0 }}>
                {order.rider.name}
              </Typography>
              {order.rider.driverType === 'RESTAURANT' ? <MicroTag>STAFF</MicroTag> : null}
            </Box>
            {order.rider.phone && (
              <Box
                component="a"
                href={`tel:${order.rider.phone}`}
                onClick={e => e.stopPropagation()}
                aria-label={`Call rider ${order.rider.phone}`}
                sx={phoneLinkSx}
              >
                <PhoneOutlinedIcon /> {order.rider.phone}
              </Box>
            )}
          </DriverStrip>
        )}

        {/* Action Buttons */}
        <Box onClick={e => e.stopPropagation()} sx={{ '&:empty': { display: 'none' } }}>
          {order.orderStatus === 'SCHEDULED' && (
            <Button
              size="small"
              fullWidth
              sx={btn.danger}
              onClick={() => onCancel && onCancel(order)}
            >
              Cancel Scheduled Order
            </Button>
          )}

          {order.orderStatus === 'PENDING' && (
            <Box display="flex" gap={1}>
              <Button
                size="small"
                variant="contained"
                disableElevation
                sx={{ ...btn.solid('#067647', '#085D3A'), flex: 1 }}
                onClick={() => onAccept && onAccept(order)}
              >
                Accept
              </Button>
              <Button
                size="small"
                sx={{ ...btn.danger, flex: 0.6 }}
                onClick={() => onCancel && onCancel(order)}
              >
                Cancel
              </Button>
            </Box>
          )}

          {(order.orderStatus === 'ACCEPTED' || order.orderStatus === 'ASSIGNED') && !order.preparedAt && (
            <Box display="flex" gap={1}>
              <Button
                size="small"
                variant="contained"
                disableElevation
                sx={{ ...btn.solid('#6938EF', '#5925DC'), flex: 1 }}
                onClick={() => onMarkPrepared && onMarkPrepared(order)}
              >
                Mark Ready
              </Button>
              <Button
                size="small"
                aria-label="Cancel order"
                title="Cancel order"
                sx={{ ...btn.danger, minWidth: 36, px: 0 }}
                onClick={() => onCancel && onCancel(order)}
              >
                <CloseRoundedIcon sx={{ fontSize: 18 }} />
              </Button>
            </Box>
          )}

          {order.orderStatus === 'PREPARED' && order.isSelfDelivery && (
            <Callout center toneName="violet" icon={<DeliveryDiningOutlinedIcon />}>
              Mark Out for Delivery
            </Callout>
          )}
          {order.orderStatus === 'PREPARED' && canHaveRider(order) && !order.isSelfDelivery && !order.firstDeliveryOrderId && (
            <Button
              size="small"
              variant="contained"
              disableElevation
              fullWidth
              sx={btn.solid('#026AA2', '#065986')}
              onClick={() => onAssignRider && onAssignRider(order)}
            >
              Assign Rider
            </Button>
          )}
          {order.orderStatus === 'PREPARED' && !order.isPickedUp && !order.isSelfDelivery && order.firstDeliveryOrderId && (
            <Callout center toneName="sky" icon={<LocalShippingOutlinedIcon />}>
              Dispatched to Delivery
            </Callout>
          )}

          {order.orderStatus === 'PREPARED' && (order.isPickedUp || inStoreLabel(order)) && (
            <Callout center toneName="violet" icon={<CheckRoundedIcon />}>
              Ready for Pickup
            </Callout>
          )}

          {order.orderStatus === 'PICKED' && order.isSelfDelivery && (
            <Callout center toneName="sky" icon={<DeliveryDiningOutlinedIcon />}>
              Out for delivery
            </Callout>
          )}
          {['ASSIGNED', 'PICKED'].includes(order.orderStatus) && !order.isSelfDelivery && (
            <Callout center toneName="sky" icon={<TwoWheelerIcon />}>
              {order.orderStatus === 'PICKED' ? 'Rider on the way' : 'Rider assigned'}
            </Callout>
          )}
        </Box>
      </CardFooter>
    </OrderCardShell>
  )
}

export default OrderCard
